import { AppBar, Box, Grid, IconButton } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import withStyles from '@mui/styles/withStyles';
import { IconBulb, IconMenu2, IconFilter, IconSearch} from '@tabler/icons-react';
import { AppConfig } from 'AppConfig';
import * as ReportTypes from 'features/frontoffice/actions/types';
import { setGlobalFilters } from 'features/globalFilters/globalFiltersReducer';
import { saveGlobalFiltersToBackend } from 'features/globalFilters/globalFiltersActions';
import 'features/landing/navigation/top/bstBars.css';
import UserMenu from 'features/landing/navigation/top/userMenu';
import { fetchData } from 'features/user/userActions';
import * as UserTypes from 'features/user/userTypes';
import { beginSwitch, registerIntent } from 'features/user/tenantSwitchSequence';
import debounce from 'lodash.debounce';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { store } from 'store/store';
import { isEmpty } from 'utils/getObjectType';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import GlobalSearchField from './GlobalSearchField';
import GlobalFiltersDrawer from './GlobalFiltersDrawer';
import TenantSwitchDrawer from './TenantSwitchDrawer';
import SideNavBar from 'features/frontoffice/component/sideNavBar';
import { DRAWER_WIDTH } from 'common/Constant';
import CustomTooltip from 'common/customTooltip';
import ActiveFiltersBanner from 'features/globalFilters/ActiveFiltersBanner';
import { parseReportViewPath, switchReportToTenant } from 'utils/reportTenantTenantSwitch';
import { showRestoreToast } from 'utils/showRestoreToast';

const styles = {
  searchPanelLightTouch: {
    boxSizing: 'border-box',
    '& input + input': {
      marginLeft: '0.5rem'
    },
    padding: 0
  },
  containerLightTouch: {
    height: '100%'
  },
  profilePanelLightTouch: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    alignSelf: 'center',
    height: '100%',
    justifyContent: 'flex-end',
    gap: '1rem'
  },
  profileHelperLightTouch: {
    display: 'flex',
    gap: '16px'
  },

  userMenuHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const popperProps = {
  modifiers: [
    {
      name: "offset",
      options: {
        offset : [-17 , -5]
      }
    }
  ]
};

const activationEngineRegex = /^\/activation-engine(?:\/|$)/;

class BstBars extends Component {
  state = {
    allBrokerUrl: null,
    allTenantUrl: null,
    allBrokers: [],
    allTenants: [],
    claimTypeOptions: null,
    currentTenants: [],
    isOpen: false,
    messages: {},
    planYears: [],
    processingEvent: false,
    searchTerms: [],
    searchBarFocused: false,
    selectedSiteCd: null,
    showAlert: false,
    subgroup1Options: null,
    subgroup2Options: null,
    subgroup3Options: null,
    tenantOptions: null,
    tpaNameOptions: null,
    isSearchBarOpen: false,
    selectedSearchOptions: [],
    anchorEl: null,
    searchValue: null,
    isMenuOpen: false,
    isGlobalFiltersOpen: false,
    isSearchDrawerOpen: false,
  };

  chartRequest = {};

  constructor(props, children) {
    super(props);
    store.subscribe(this.bstBarDispatchDone.bind(this));
    this.myRef = React.createRef();
    this.location = props.location;
    this.navigate = props.navigate;
    this.pendingTenantCd = props.currentTenantCd;
  }

  bstBarDispatchDone = () => {
    if (store.getState().entities.User && !isEmpty(store.getState().entities.User.itemsById)) {
      if (this.props && this.props.message) {
        this.setState({ showAlert: true });
      } else {
        this.setState({ showAlert: false });
      }
    }
  };

  toggleMenu = (open) => {
    this.setState({ isMenuOpen: open });
  };

  toggleGlobalFilters = (open) => {
    this.setState({ isGlobalFiltersOpen: open });
  };

  toggleSearchDrawer = (open) => {
    this.setState({ isSearchDrawerOpen: open });
  };

  resetProfileSelections = (types) => {
    if (!types || types.length === 0) {
      return;
    }
    types.forEach((item) => {
      store.dispatch({
        type: item,
        payload: null
      });
    });
  };

  handleBrokerSelection = debounce((value) => {
    if (!value) {
      return value;
    }
    store.dispatch({
      type: UserTypes.BROKER_SET,
      payload: value.text
    });
  }, AppConfig.DEBOUNCE_WAIT_TIME);

  handleSiteSelection = debounce((value) => {
    trackClick(trackingTypes.TENANT, trackingActions.SWITCH, value?.code);
    store.dispatch(fetchData(value.code, this.props.currentApp, { explicitApp: false }));
  }, AppConfig.DEBOUNCE_WAIT_TIME);

  pendingReportSwitch = null;
  // Flip the app into the switching state as soon as a tenant is picked, not when the
  // debounced request finally fires -- otherwise tenant-scoped screens keep rendering the
  // outgoing tenant's data for the debounce interval on top of the request round trip.
  beginSiteSelection = (tenant) => {
    // Claim the switch sequence NOW, not when the debounced request fires 500ms later.
    // Raising isUpdating alone is not enough: an earlier switch still in flight would
    // remain the latest, commit its tenant/JWT on arrival and drop the guard, so Enigma
    // could remount on a tenant the user has already left. fetchData claims again when
    // it runs; the id taken here exists only to supersede everything before it.
    beginSwitch();
    // Also make this tenant the live intent now, so an app-context switch that starts
    // during the 500ms debounce (e.g. a route initialiser) asks for THIS tenant instead of
    // echoing the committed one and silently discarding the selection.
    registerIntent({ tenantCd: tenant.code });
    this.pendingTenantCd = tenant.code;
    store.dispatch({ type: UserTypes.TENANT_SWITCH_START });
    this.handleSiteSelection(tenant);
  };

  handleGlobalFiltersApply = (filters) => {
    const { tenant, ...filterValues } = filters;
    store.dispatch(setGlobalFilters(filterValues));

    // Persist the applied filter for the tenant it applies to so it can be
    // restored when the user returns to that tenant.
    const appliedTenantCd = tenant?.code || this.props.currentTenantCd;
    store.dispatch(saveGlobalFiltersToBackend(appliedTenantCd, filterValues));

    if (
      tenant?.code &&
      (tenant.code !== this.props.currentTenantCd || tenant.code !== this.pendingTenantCd)
    ) {
      const pathname = window?.location?.pathname;
      const isReportView = Boolean(parseReportViewPath(pathname));

      this.pendingReportSwitch = isReportView && tenant.code !== this.props.currentTenantCd ? { tenant, pathname } : null;

      this.beginSiteSelection(tenant);
    }

    this.toggleGlobalFilters(false);
  };

  componentDidUpdate(){
    const pending = this.pendingReportSwitch;

    if(pending && this.props.currentTenantCd === pending.tenant.code) {
      this.pendingReportSwitch = null;

      switchReportToTenant({  
        pathname: pending.pathname,
        newTenant: pending.tenant,
        currentReportTitle: store.getState().fo.currentReportTitle,
        tableauServiceUrl: store.getState().user.tableauServiceUrl,
        navigate: this.goTo,
        dispatch: store.dispatch,
        showToast: this.showToast
      });
    }
  }

  goTo = (path, options = {}) => {
    if(this.props.navigate) {
      this.props.navigate(path, options); 
    } else if(this.props.history) {
      options.replace ? this.props.history.replace(path) : this.props.history.push(path);
    } else {
      window.location.assign(path);
    }
  };

  showToast = (message = '') => {
    const EXTRA_SPACE = 100;
    const CHAR_WIDTH = 7.5;
    const MIN_WIDTH = 320;
    const MAX_WIDTH = Math.min(640, window?.innerWidth - 32);

    const textPx = Math.ceil(message.length * CHAR_WIDTH);
    const toastWidth = Math.min(Math.max(textPx + EXTRA_SPACE, MIN_WIDTH), MAX_WIDTH);
    const textWidth = toastWidth - EXTRA_SPACE;
    showRestoreToast(message, `${toastWidth}px`, `${textWidth}px`, '#88041E', true);
  };
  
  handleTenantSwitch = (tenant) => {
    if (
      tenant?.code &&
      (tenant.code !== this.props.currentTenantCd || tenant.code !== this.pendingTenantCd)
    ) {
      this.beginSiteSelection(tenant);
    }

    this.toggleGlobalFilters(false);
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.processingEvent === true && nextState.processingEvent === true) {
      return false;
    }
    return true;
  }

  filterOptions = createFilterOptions({
    stringify: (option) => `${option.name} ${option.code}`
  });

  searchFilterOptions = createFilterOptions({
    stringify: (option) => `${option.searchTerm}`
  });

  searchOptionSelected = (searchOptionValue) => {
    // Extract parts from the URL
    const url = new URL(searchOptionValue.reportSource ? searchOptionValue.reportSource : searchOptionValue.reptSource);
    const pathSegments = url.pathname.split('/');

    // Extract currentTenantCd, workbook, and dashboard
    const currentTenantCd = pathSegments[4];
    const workbook = pathSegments[6];
    const dashboard = pathSegments[7];

    const reportBaseUrl = window?.location?.pathname;
    // Generate the desired path
    const generatedPath = `${reportBaseUrl}/${currentTenantCd}/view/${workbook}/${dashboard}`;
    this.navigate(generatedPath);

    store.dispatch({
      type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
      reportUrl: searchOptionValue.reportSource ? searchOptionValue.reportSource : searchOptionValue.reptSource,
      category: searchOptionValue.category,
      title: searchOptionValue.displayText,
      reportBaseUrl,
      report: searchOptionValue
    });
    this.setState({
      ...this.state,
      selectedSearchOptions: [searchOptionValue]
    });
    sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl }));
  };

  getUniqueMenuSearchOptions = () => {
    const { menuSearchOptions } = this.props;
    const seen = new Set();
    return (menuSearchOptions || []).filter((option) => {
      const key = option.displayText;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  shouldShowSearchBar = (pathname) => {
	const cohortBuilderRegex = /^\/cohort-builder\$*/;
    const biReportRegex = /^\/bi\/[^/]+\/view\/[^/]+\/[^/]+$/;
    return !(cohortBuilderRegex.test(pathname) || biReportRegex.test(pathname) || activationEngineRegex.test(pathname));
  };

  render() {
    const { currentTenantCd, companies, classes } = this.props;
    const pathname = this.props.location?.pathname || '';
    const isActivationEngineRoute = activationEngineRegex.test(pathname);
    const checkBannerPath = pathname === '/bi' || pathname === '/bi/' || pathname.startsWith('/bi/smart-cards') || pathname.startsWith('/bi/reports');
    const isFOHeaderType = this.props.headerType === AppConfig.HEADER_MENU_TYPE.FO;
	  const cohortBuilderRegex = /^\/cohort-builder(?:\/|$)/;
    const showGlobalFiltersButton = (isFOHeaderType || isActivationEngineRoute) && !cohortBuilderRegex.test(pathname);
	  const showTenantFilter =
      (AppConfig.HEADER_MENU_TYPE.STANDARD === this.props.headerType || isFOHeaderType) &&
      this.props.showBstBarTenant === true;

    this.chartRequest.tenant = this.props.currentTenantCd;

    return (
      <>
        <AppBar
          position="fixed"
          sx={{
            background: { xs: '#001A47', sm: '#001A47', md: '#ffffff' },
            boxShadow: 'none',
            border: 'none',
            outline: 'none',
            '&.MuiAppBar-root': {
              border: 'none !important',
              outline: 'none'
            }
          }}>
          <Box
            sx={{
              width: {
                xs: '100%',
                sm: '100%',
                md: `calc(100% - ${DRAWER_WIDTH}px)`
              },
              ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
              px: { xs: 2, sm: 6 },
              py: 3,
              height: { xs: '80px', md: '92px' },
              boxSizing: 'border-box',
              borderBottom: { xs: 'none', md: '1px solid #71767A' },
            }}>
            <Grid container className={classes.containerLightTouch} ref={this.myRef}>
              <Grid
                item
                xs="auto"
                sx={{
                  display: { xs: 'flex', sm: 'flex', md: 'none' },
                  width: { xs: '109px', sm: '134px' },
                  height: { xs: '24px', sm: '32px' },
                  alignSelf: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  sx={{ 
                    width: { xs: '100px', sm: '134px' },
                    objectFit: 'contain', 
                    height: 'auto', 
                    display: 'block' 
                  }}
                  src={`${AppConfig.FO_INITIAL_URL}/open-api/logo/v2/tenant/${currentTenantCd}/dark?cb=${this.props.logoCacheBuster}`}
                  alt="BST"
                  onClick={() => this.navigate('/')}
                />
              </Grid>

              <Grid item
                xs={4} 
                className={classes.searchPanelLightTouch} 
                sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {isFOHeaderType && this.shouldShowSearchBar(pathname) && (
                    <GlobalSearchField currentTenantCd={currentTenantCd} />
                  )}
                </Box>
              </Grid>
              <Grid item xs md={8}>
                <Box className={classes.profilePanelLightTouch}>
                  {(isFOHeaderType || isActivationEngineRoute) && (
                    <Box className={classes.profileHelperLightTouch}>
                      {showGlobalFiltersButton && (
                      <IconButton
                        onClick={() => this.toggleGlobalFilters(true)}
                        sx={{
                          display: { xs: 'none', sm: 'none', md: 'flex' },
                          border: '1px solid var(--ctev-blue, #2F54A5)',
                          borderRadius: '24px',
                          py: 0,
                          px: '16px',
                          backgroundColor: '#ffffff',
                          color: '#2F54A5',
                          fontWeight: '600',
                          fontSize: '16px',
                          lineHeight: '24px',
                          height: '44px',
                          fontFamily: 'Noto Sans',
                          '&:hover': {
                            backgroundColor: '#D8E6FF',
                            color: '#001A47',
                            border: '1px solid #001A47',
                          },
                        }}
                      >
                        <IconFilter size={20}  style={{ marginRight: 8 }} />
                        Filters
                      </IconButton>
                      )}
                      <CustomTooltip title="Idea Portal" arrow borderRadius="8px 0px 8px 8px" popperProps={popperProps}>
                        <IconButton
                          id="expand-ideas-menu"
                          sx={{
                            '&:hover': { 
                              outline: 'none',
                              backgroundColor: '#D8E6FF',
                              color: '#001A47',
                              border: '1px solid #001A47',
                            },
                            '&:focus': { outline: 'none' },
                            '&:focus-visible': { outline: 'none' },
                            border: '1px solid var(--ctev-blue, #2F54A5)',
                            color: '#2F54A5',
                            borderRadius: '50%',
                            alignSelf: 'center',
                            width: 44,
                            height: 44,
                            display: {
                              xs: 'none',
                              md: 'flex',
                              sm: 'none',
                            },
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: '3px'
                          }}
                          onClick={() => {
                            window.open('https://beninsightsprivate.ideas.aha.io/ideas/new', '_blank');
                          }}
                          size="small">
                          <IconBulb stroke={2} size={20} />
                        </IconButton>
                      </CustomTooltip>
                      <Box className={classes.userMenuHeader}>
                        <UserMenu />
                      </Box>
                    </Box>
                  )}

                  {/* Tablet and Mobile Viewports */}
                  <Box
                    sx={{
                      display: {
                        xs: 'flex',
                        sm: 'flex',
                        md: 'none',
                      },
                      cursor: 'pointer',
                      color: '#FFF',
                      gap: { xs: '16px', sm: '24px' },
                      alignItems: 'center',
                    }}
                  >
                    {isFOHeaderType && this.shouldShowSearchBar(pathname) && (
                      <IconButton
                        aria-label="Open search"
                        disableRipple
                        onClick={() => this.toggleSearchDrawer(true)}
                        sx={{ color: 'inherit', p: 0, '&:hover': { backgroundColor: 'transparent' } }}
                      >
                        <IconSearch size={24} />
                      </IconButton>
                    )}
                    {showGlobalFiltersButton && (
                      <IconButton
                        aria-label="Open filters"
                        disableRipple
                        onClick={() => this.toggleGlobalFilters(true)}
                        sx={{ color: 'inherit', p: 0, '&:hover': { backgroundColor: 'transparent' } }}
                      >
                        <IconFilter size={24} />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="Open menu"
                      disableRipple
                      onClick={() => this.toggleMenu(true)}
                      sx={{ color: 'inherit', p: 0, '&:hover': { backgroundColor: 'transparent' } }}
                    >
                      <IconMenu2 size={24} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <SideNavBar
              currentTenantCd={currentTenantCd}
              open={this.state.isMenuOpen}
              onClose={() => this.toggleMenu(false)}
            />
            {this.shouldShowSearchBar(pathname) && (
              <GlobalSearchField
                currentTenantCd={currentTenantCd}
                tabletMobileMode
                drawerOpen={this.state.isSearchDrawerOpen}
                onDrawerClose={() => this.toggleSearchDrawer(false)}
            />)}

            {isActivationEngineRoute ? (
              <TenantSwitchDrawer
                companies={companies}
                currentTenantCd={currentTenantCd}
                filterOptions={this.filterOptions}
                onApply={this.handleTenantSwitch}
                onClose={() => this.toggleGlobalFilters(false)}
                open={this.state.isGlobalFiltersOpen}
                showTenantFilter={showTenantFilter}
              />
            ) : (
              <GlobalFiltersDrawer
                appliedFilters={this.props.appliedGlobalFilters}
                companies={companies}
                currentTenantCd={currentTenantCd}
                cohortBuilderFlowId={this.props.cohortBuilderFlowId}
                cohortBuilderTenantCd={this.props.cohortBuilderTenantCd}
                filterOptions={this.filterOptions}
                isReportView={currentTenantCd && pathname.includes(`/${currentTenantCd}/view/`)}
                onApply={this.handleGlobalFiltersApply}
                onClose={() => this.toggleGlobalFilters(false)}
                open={this.state.isGlobalFiltersOpen}
                showTenantFilter={showTenantFilter}
                returnFromCohortBuilder={this.props.returnFromCohortBuilder}
                cohortWasPublished={this.props.cohortWasPublished}
                onCohortBuilderReturnConsumed={this.props.onCohortBuilderReturnConsumed}
              />
            )}
          </Box>
          {checkBannerPath && <ActiveFiltersBanner />}
        </AppBar>
      </>
    );
  }
}

const BstBarsWithHooks = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [returnFromCohortBuilder, setReturnFromCohortBuilder] = React.useState(false);
  const [cohortBuilderFlowId, setCohortBuilderFlowId] = React.useState(null);
  const [cohortBuilderTenantCd, setCohortBuilderTenantCd] = React.useState(null);
  const [cohortWasPublished, setCohortWasPublished] = React.useState(false);
  const bstBarsRef = React.useRef(null);
  // Track the navigation key of the history entry that was already consumed so
  // pressing browser Back to a stale entry with returnFromCohortBuilder:true does
  // not re-trigger the return flow a second time.
  const consumedCohortReturnKeyRef = React.useRef(null);

  React.useEffect(() => {
    if (location.state?.returnFromCohortBuilder && consumedCohortReturnKeyRef.current !== location.key) {
      consumedCohortReturnKeyRef.current = location.key;
      setReturnFromCohortBuilder(true);
      setCohortBuilderFlowId(typeof location.state?.cohortBuilderFlowId === 'string' ? location.state.cohortBuilderFlowId : null);
      setCohortBuilderTenantCd(typeof location.state?.tenantCd === 'string' ? location.state.tenantCd : null);
      setCohortWasPublished(location.state?.cohortWasPublished === true);

      // Clear only the Cohort Builder flag so other navigation state (e.g. tenantCd) is preserved.
      const { returnFromCohortBuilder: _ignored, cohortWasPublished: _ignored2, cohortBuilderFlowId: _ignored3, ...nextState } = location.state || {};
      navigate({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      }, { replace: true, state: nextState });

      // Auto-open the global filters drawer
      if (bstBarsRef.current) {
        bstBarsRef.current.toggleGlobalFilters(true);
      }
    }
  }, [location.key, location.state, location.pathname, location.search, location.hash, navigate]);

  const handleCohortBuilderReturnConsumed = React.useCallback(() => {
    setReturnFromCohortBuilder(false);
    setCohortBuilderFlowId(null);
    setCohortBuilderTenantCd(null);
    setCohortWasPublished(false);
  }, []);

  return (
    <BstBars
      {...props}
      ref={bstBarsRef}
      location={location}
      navigate={navigate}
      returnFromCohortBuilder={returnFromCohortBuilder}
      cohortBuilderFlowId={cohortBuilderFlowId}
      cohortBuilderTenantCd={cohortBuilderTenantCd}
      cohortWasPublished={cohortWasPublished}
      onCohortBuilderReturnConsumed={handleCohortBuilderReturnConsumed}
    />
  );
};

const mapStateToPropsSelector = createStructuredSelector({
  appliedGlobalFilters: (state) => state.globalFilters?.applied,
  currentTenantCd: (state) => state.user.currentTenantCd,
  currentApp: (state) => state.user.currentApp,
  menuSearchOptions: (state) => state.fo.menuSearchOptions,
  companies: (state) => state.user.companies,
  headerType: (state) => state.user.headerType,
  allBrokers: (state) => state.user.allBrokers,
  logoCacheBuster: (state) => state.whiteLabeling?.logoCacheBuster || '',
  openReportViewDialog: (state) => state.fo.openReportViewDialog
});

export default connect(mapStateToPropsSelector)(withStyles(styles)(BstBarsWithHooks));
